/* eslint-disable jira/import-whitelist */
import { loadQuery, type PreloadedQuery } from 'react-relay';
import getRelayEnvironment from '@jira/platform__graphql-relay-environment/src/index.tsx';
import { RELAY_RESOURCE_TYPE } from '@jira/platform__graphql-relay-utils/src/services/resources/index.tsx';
import HydrationRelayQuery, {
	type useHydrationQueryLoaderQuery as HydrationRelayQueryType,
} from '@jira/jql-builder__common/src/ui/hydrate-jql-builder/__generated__/useHydrationQueryLoaderQuery.graphql';
import { QueryPromisesMap } from '@atlassian/relay-query-promises';
import { createResource, type RouteResource } from '@atlassian/react-resource-router';

export const RESOURCE_TYPE_QUEUES_JQL_BUILDER_HYDRATION_RELAY =
	'queues-jql-builder-hydration-relay';

/**
 * We are using `createResource` instead of `createRelayResource` because
 * the `getKey` provided by `createRelayResource` triggers this resource to reload every time a URL parameters change
 * causes the query variables to change, preventing us from using our own custom reload hooks that are further down
 * in Issue Navigator. This results in a full-page loading state any time one of the page params changes.
 */
export const queuesJqlBuilderHydrationQueryRelayResource: RouteResource<
	PreloadedQuery<HydrationRelayQueryType, Record<string, unknown>>
> = createResource<PreloadedQuery<HydrationRelayQueryType>>({
	type: `${RELAY_RESOURCE_TYPE}_${RESOURCE_TYPE_QUEUES_JQL_BUILDER_HYDRATION_RELAY}`,
	getKey: () => RESOURCE_TYPE_QUEUES_JQL_BUILDER_HYDRATION_RELAY,
	getData: async (routerContext, { tenantContext }) => {
		const queryReference = loadQuery<HydrationRelayQueryType>(
			getRelayEnvironment(),
			HydrationRelayQuery,
			{
				accountId: tenantContext.atlassianAccountId ?? '',
				cloudId: tenantContext.cloudId,
				includeUser: true,
				includeJira: true,
				viewContext: null,
				input: { jql: '' },
			},
			{
				fetchPolicy: 'network-only',
				networkCacheConfig: {
					metadata: { META_SLOW_ENDPOINT: true },
				},
			},
		);

		if (__SERVER__ && queryReference.id != null && QueryPromisesMap.get(queryReference.id)) {
			await QueryPromisesMap.get(queryReference.id);
		}

		return queryReference;
	},
	maxAge: 0,
});
